import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { PermissionGate } from '@/shared/components/PermissionGate';
import { useNotification } from '@/shared/hooks/useNotification';
import { useApiError } from '@/shared/hooks/useApiError';
import { useCreateFinancialYear, useFinancialYears } from '../hooks/useFinancialYears';
import { FinancialYearStatus, type FinancialYearSummaryDto, type CreateFinancialYearRequest } from '../services/financialYearsApi';
import { AlertTriangle, ArrowRight, Calendar, ChevronRight, GitCompare, Lock, Plus } from 'lucide-react';
import { CreateFinancialYearDialog } from '../components/financialYears/CreateFinancialYearDialog';

const STATUS_BADGE: Record<FinancialYearStatus, { label: string; className: string }> = {
  [FinancialYearStatus.PendingApproval]: { label: 'Pending approval', className: 'bg-orange-100 text-orange-800 border-orange-300' },
  [FinancialYearStatus.Open]:            { label: 'Open',             className: 'bg-green-100 text-green-800 border-green-300' },
  [FinancialYearStatus.Closing]:         { label: 'Closing',          className: 'bg-amber-100 text-amber-800 border-amber-300' },
  [FinancialYearStatus.Closed]:          { label: 'Closed',           className: 'bg-blue-100 text-blue-800 border-blue-300' },
  [FinancialYearStatus.Locked]:          { label: 'Locked',           className: 'bg-purple-100 text-purple-800 border-purple-300' },
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' });

const StatusBadge = ({ status }: { status: FinancialYearStatus }) => {
  const cfg = STATUS_BADGE[status];
  return (
    <Badge variant="outline" className={cfg.className}>
      {status === FinancialYearStatus.Locked && <Lock className="size-3 mr-1" />}
      {cfg.label}
    </Badge>
  );
};

export const FinancialYearsPage = () => {
  const navigate = useNavigate();
  const notify = useNotification();
  const reportError = useApiError();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: years, isLoading, error } = useFinancialYears();
  const createMutation = useCreateFinancialYear();

  if (isLoading) return <LoadingAnimation fullScreen />;
  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">Failed to load financial years.</div>;

  const rows = years ?? [];
  const current = rows.find((r) => r.isCurrent);
  const pending = rows.filter((r) => r.status === FinancialYearStatus.PendingApproval);

  const handleCreate = (payload: CreateFinancialYearRequest) => {
    createMutation.mutate(payload, {
      onSuccess: (fy) => {
        notify.success(`${fy.label} created.`);
        setCreateOpen(false);
        navigate(`/accounting/financial-years/${fy.id}`);
      },
      onError: (err) => reportError(err),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="size-6" /> Financial Years
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage close cadence — review the pre-close checklist, close a year (irreversible), and lock once audited.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/accounting/financial-years/compare')}>
            <GitCompare className="size-4 mr-2" /> Compare years
          </Button>
          <PermissionGate permissions={['financial_years.create']}>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4 mr-2" /> New financial year
            </Button>
          </PermissionGate>
        </div>
      </div>

      {pending.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-orange-900">
              <AlertTriangle className="size-5" /> {pending.length} financial {pending.length === 1 ? 'year is' : 'years are'} awaiting approval
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-orange-900/80">
              A General Manager must approve creation before posting can land in these years.
            </p>
            <ul className="space-y-1">
              {pending.map((fy) => (
                <li key={fy.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/accounting/financial-years/${fy.id}`)}
                    className="text-orange-900 underline hover:no-underline inline-flex items-center gap-1"
                  >
                    <strong>{fy.label}</strong> — review &amp; approve
                    <ArrowRight className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {current && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Current financial year</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{current.label}</div>
                <div className="text-sm text-muted-foreground">
                  {formatDate(current.startDate)} → {formatDate(current.endDate)}
                </div>
              </div>
              <StatusBadge status={current.status} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All years</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Year</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <FinancialYearRow key={row.id} row={row} onOpen={() => navigate(`/accounting/financial-years/${row.id}`)} />
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No financial years yet. Click <strong>New financial year</strong> above to create the first one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <CreateFinancialYearDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        submitting={createMutation.isPending}
      />
    </div>
  );
};

const FinancialYearRow = ({ row, onOpen }: { row: FinancialYearSummaryDto; onOpen: () => void }) => (
  <TableRow className="cursor-pointer hover:bg-muted/40" onClick={onOpen}>
    <TableCell className="font-semibold">
      {row.label} {row.isCurrent && <Badge variant="outline" className="ml-2 text-xs">Current</Badge>}
    </TableCell>
    <TableCell className="text-sm">{formatDate(row.startDate)} → {formatDate(row.endDate)}</TableCell>
    <TableCell><StatusBadge status={row.status} /></TableCell>
    <TableCell className="text-right">
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
        Open <ChevronRight className="size-4 ml-1" />
      </Button>
    </TableCell>
  </TableRow>
);
